/*
 * Live product star ratings, backed by an external ratings API.
 *
 * - One batched summary request per hydration wave (initial page load plus
 *   late-injected cards such as product recommendations).
 * - Fills Dawn's existing rating markup rendered by snippets/rating-stars.liquid.
 * - Powers the interactive 1-5 star picker on the product page.
 *
 * If the API is unreachable or slow, the page simply stays as rendered:
 * summaries remain hidden and the picker stays usable for a retry.
 */
(function () {
  'use strict';

  var configEl = document.getElementById('ProductRatingsConfig');
  if (!configEl) return;

  var config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch (error) {
    return;
  }
  if (!config || !config.apiUrl) return;

  var apiBase = String(config.apiUrl).replace(/\/+$/, '');
  var strings = config.strings || {};
  var MAX_IDS_PER_REQUEST = 100;

  var FINGERPRINT_KEY = 'ls-rating-fingerprint';
  var RATED_KEY_PREFIX = 'ls-rating-value-';

  function storageGet(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  function storageSet(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (error) {
      /* private mode etc. - re-rating will just insert under a new fingerprint */
    }
  }

  function getFingerprint() {
    var existing = storageGet(FINGERPRINT_KEY);
    if (existing && existing.length >= 8) return existing;
    var fingerprint = '';
    if (window.crypto && window.crypto.getRandomValues) {
      var bytes = new Uint8Array(16);
      window.crypto.getRandomValues(bytes);
      for (var i = 0; i < bytes.length; i++) {
        fingerprint += ('0' + bytes[i].toString(16)).slice(-2);
      }
    } else {
      fingerprint =
        'fp' + Date.now().toString(36) + Math.random().toString(36).slice(2, 12);
    }
    storageSet(FINGERPRINT_KEY, fingerprint);
    return fingerprint;
  }

  function format(template, replacements) {
    var out = String(template || '');
    Object.keys(replacements).forEach(function (token) {
      out = out.split('[[' + token + ']]').join(String(replacements[token]));
    });
    return out;
  }

  function formatAverage(average) {
    var rounded = Math.round(Number(average) * 10) / 10;
    return String(rounded);
  }

  function starVars(average) {
    var whole = Math.floor(average);
    var decimal = average % 1;
    var ratingDecimal = 0;
    if (decimal >= 0.3 && decimal <= 0.7) ratingDecimal = 0.5;
    else if (decimal > 0.7) ratingDecimal = 1;
    return { rating: whole, decimal: ratingDecimal };
  }

  function applySummary(container, summary) {
    if (!summary) return;
    container.setAttribute('data-rating-loaded', 'true');
    var count = Number(summary.count) || 0;
    var average = Number(summary.average) || 0;
    var summaryEl = container.querySelector('[data-rating-summary]');
    if (!summaryEl) return;

    if (count <= 0) {
      summaryEl.hidden = true;
      return;
    }

    var vars = starVars(average);
    var star = summaryEl.querySelector('[data-rating-star]');
    if (star) {
      star.style.setProperty('--rating', String(vars.rating));
      star.style.setProperty('--rating-max', '5');
      star.style.setProperty('--rating-decimal', String(vars.decimal));
    }
    var visual = summaryEl.querySelector('[data-rating-visual]');
    if (visual) {
      visual.setAttribute(
        'aria-label',
        format(strings.summaryLabel, { rating: formatAverage(average), max: 5 })
      );
    }
    var averageEl = summaryEl.querySelector('[data-rating-average]');
    if (averageEl) averageEl.textContent = formatAverage(average);
    var countEl = summaryEl.querySelector('[data-rating-count]');
    if (countEl) countEl.textContent = String(count);
    var countSrEl = summaryEl.querySelector('[data-rating-count-sr]');
    if (countSrEl) countSrEl.textContent = String(count);
    summaryEl.hidden = false;
  }

  function pendingContainers() {
    return Array.prototype.filter.call(
      document.querySelectorAll('[data-rating-product-id]'),
      function (el) {
        return !el.hasAttribute('data-rating-requested');
      }
    );
  }

  function hydrate() {
    var containers = pendingContainers();
    if (containers.length === 0) return;

    var ids = [];
    containers.forEach(function (el) {
      el.setAttribute('data-rating-requested', 'true');
      var id = el.getAttribute('data-rating-product-id');
      if (id && /^\d+$/.test(id) && ids.indexOf(id) === -1) ids.push(id);
    });
    if (ids.length === 0) return;

    var chunks = [];
    for (var i = 0; i < ids.length; i += MAX_IDS_PER_REQUEST) {
      chunks.push(ids.slice(i, i + MAX_IDS_PER_REQUEST));
    }

    chunks.forEach(function (chunk) {
      fetch(apiBase + '/ratings/summary?ids=' + chunk.join(','))
        .then(function (response) {
          if (!response.ok) throw new Error('ratings summary failed');
          return response.json();
        })
        .then(function (payload) {
          var summaries = (payload && payload.summaries) || {};
          document
            .querySelectorAll('[data-rating-product-id]')
            .forEach(function (container) {
              var id = container.getAttribute('data-rating-product-id');
              if (summaries[id]) applySummary(container, summaries[id]);
            });
        })
        .catch(function () {
          /* Leave the page as it was. */
        });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Interactive picker (product page)                                   */
  /* ------------------------------------------------------------------ */

  function setStarFill(buttons, upTo) {
    buttons.forEach(function (button, index) {
      button.classList.toggle('rating-live__star-button--filled', index < upTo);
    });
  }

  function setSelected(buttons, value) {
    buttons.forEach(function (button, index) {
      button.setAttribute('aria-checked', index + 1 === value ? 'true' : 'false');
    });
  }

  function initPicker(container) {
    if (container.hasAttribute('data-rating-picker-ready')) return;
    var picker = container.querySelector('[data-rating-picker]');
    if (!picker) return;
    container.setAttribute('data-rating-picker-ready', 'true');

    var productId = container.getAttribute('data-rating-product-id');
    var productHandle = container.getAttribute('data-rating-product-handle');
    var buttons = Array.prototype.slice.call(
      picker.querySelectorAll('[data-rating-value]')
    );
    var statusEl = picker.querySelector('[data-rating-status]');
    var promptEl = picker.querySelector('[data-rating-prompt]');
    var submitting = false;

    var storedValue = parseInt(storageGet(RATED_KEY_PREFIX + productId), 10);
    var currentRating =
      storedValue >= 1 && storedValue <= 5 ? storedValue : 0;

    function reflectCurrent() {
      setStarFill(buttons, currentRating);
      setSelected(buttons, currentRating);
      if (currentRating > 0 && promptEl && strings.yourRating) {
        promptEl.textContent = format(strings.yourRating, {
          rating: currentRating
        });
      }
    }

    function preview(value) {
      setStarFill(buttons, value);
    }

    function submit(value) {
      if (submitting) return;
      submitting = true;
      var alreadyRated = currentRating > 0;
      buttons.forEach(function (button) {
        button.disabled = true;
      });

      fetch(apiBase + '/ratings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productId: productId,
          productHandle: productHandle || undefined,
          rating: value,
          fingerprint: getFingerprint(),
          website: ''
        })
      })
        .then(function (response) {
          if (!response.ok) throw new Error('rating submit failed');
          return response.json();
        })
        .then(function (payload) {
          currentRating = value;
          storageSet(RATED_KEY_PREFIX + productId, String(value));
          reflectCurrent();
          if (payload && payload.summary) {
            applySummary(container, payload.summary);
          }
          if (statusEl) {
            statusEl.textContent = alreadyRated
              ? format(strings.updated, { rating: value })
              : format(strings.thanks, { rating: value });
          }
        })
        .catch(function () {
          if (statusEl) statusEl.textContent = strings.error || '';
          reflectCurrent();
        })
        .then(function () {
          submitting = false;
          buttons.forEach(function (button) {
            button.disabled = false;
          });
        });
    }

    buttons.forEach(function (button) {
      var value = parseInt(button.getAttribute('data-rating-value'), 10);
      button.addEventListener('mouseenter', function () {
        if (!submitting) preview(value);
      });
      button.addEventListener('focus', function () {
        if (!submitting) preview(value);
      });
      button.addEventListener('blur', function () {
        if (!submitting) reflectCurrent();
      });
      button.addEventListener('click', function () {
        submit(value);
      });
    });
    picker.addEventListener('mouseleave', function () {
      if (!submitting) reflectCurrent();
    });

    reflectCurrent();
  }

  function initPickers() {
    document
      .querySelectorAll('[data-rating-interactive]')
      .forEach(initPicker);
  }

  /* ------------------------------------------------------------------ */
  /* Boot                                                                */
  /* ------------------------------------------------------------------ */

  var scheduled = null;
  function scheduleHydration() {
    if (scheduled) return;
    scheduled = window.setTimeout(function () {
      scheduled = null;
      initPickers();
      hydrate();
    }, 150);
  }

  function start() {
    initPickers();
    hydrate();

    /* Late-injected cards (e.g. product recommendations) get batched into
       a follow-up request instead of one request per card. */
    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (
            node.nodeType === 1 &&
            (node.hasAttribute('data-rating-product-id') ||
              node.querySelector('[data-rating-product-id]'))
          ) {
            scheduleHydration();
            return;
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
